import React, { useMemo, useState } from 'react';
import {
  format, addDays, addWeeks, addMonths, subDays, subWeeks, subMonths,
  startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval,
  isSameDay, isSameMonth, isToday, parseISO,
} from 'date-fns';
import { ChevronLeft, ChevronRight, CalendarIcon, Clock, ExternalLink } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader } from '../ui/card';
import { HoverCard, HoverCardTrigger, HoverCardContent } from '../ui/hover-card';
import { Badge } from '../ui/badge';
import { linkifyReminderText, extractPrimaryLink } from '../../lib/reminderLinks';

const CATEGORY_COLORS = {
  Personal:  'bg-blue-100  text-blue-700  border-blue-300',
  Job:       'bg-amber-100 text-amber-700 border-amber-300',
  Equipment: 'bg-green-100 text-green-700 border-green-300',
  Meeting:   'bg-purple-100 text-purple-700 border-purple-300',
  '811':     'bg-red-100   text-red-700   border-red-300',
  Other:     'bg-slate-100 text-slate-700 border-slate-300',
};

/**
 * Reminders calendar with Month / Week / Day view options, hover preview,
 * and drag-to-reschedule. Mirrors a subset of the main Calendar's UX so
 * office workers don't have to learn a separate paradigm for personal
 * reminders.
 *
 * Props:
 *   reminders         array of reminder records ({ id, title, description, date, time, category, priority, completed })
 *   anchorDate        the date to anchor the visible window
 *   onAnchorChange    (newDate) => void
 *   onDayClick        (Date) => void   — clicking an empty day creates a reminder
 *   onReminderClick   (reminder) => void — clicking a chip opens edit
 *   onReminderMove    (reminder, newDateStr) => void — fired on drop
 */
export default function RemindersCalendar({
  reminders = [],
  anchorDate,
  onAnchorChange,
  onDayClick,
  onReminderClick,
  onReminderMove,
}) {
  const [view, setView] = useState('month');     // 'month' | 'week' | 'day'
  const [dragId, setDragId] = useState(null);

  const visibleDays = useMemo(() => {
    if (view === 'day') return [anchorDate];
    if (view === 'week') {
      return eachDayOfInterval({
        start: startOfWeek(anchorDate, { weekStartsOn: 0 }),
        end:   endOfWeek(anchorDate,   { weekStartsOn: 0 }),
      });
    }
    // month
    const gridStart = startOfWeek(startOfMonth(anchorDate), { weekStartsOn: 0 });
    const gridEnd   = endOfWeek(endOfMonth(anchorDate),     { weekStartsOn: 0 });
    return eachDayOfInterval({ start: gridStart, end: gridEnd });
  }, [view, anchorDate]);

  const remindersByDay = useMemo(() => {
    const map = {};
    for (const r of reminders) {
      if (!r.date) continue;
      map[r.date] = map[r.date] || [];
      map[r.date].push(r);
    }
    return map;
  }, [reminders]);

  const goPrev = () => {
    if (view === 'day')   onAnchorChange(subDays(anchorDate, 1));
    if (view === 'week')  onAnchorChange(subWeeks(anchorDate, 1));
    if (view === 'month') onAnchorChange(subMonths(anchorDate, 1));
  };
  const goNext = () => {
    if (view === 'day')   onAnchorChange(addDays(anchorDate, 1));
    if (view === 'week')  onAnchorChange(addWeeks(anchorDate, 1));
    if (view === 'month') onAnchorChange(addMonths(anchorDate, 1));
  };

  const titleLabel = useMemo(() => {
    if (view === 'day')  return format(anchorDate, 'EEEE, MMMM d, yyyy');
    if (view === 'week') {
      const s = startOfWeek(anchorDate, { weekStartsOn: 0 });
      const e = endOfWeek(anchorDate, { weekStartsOn: 0 });
      return `${format(s, 'MMM d')} – ${format(e, 'MMM d, yyyy')}`;
    }
    return format(anchorDate, 'MMMM yyyy');
  }, [view, anchorDate]);

  const handleDrop = (e, day) => {
    e.preventDefault();
    const id = e.dataTransfer.getData('text/reminder-id');
    if (!id) return;
    const newDate = format(day, 'yyyy-MM-dd');
    const moved = reminders.find(r => r.id === id);
    if (!moved || moved.date === newDate) return;
    onReminderMove?.(moved, newDate);
  };

  return (
    <Card data-testid="reminders-calendar">
      <CardHeader className="pb-3">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={goPrev} data-testid="rem-cal-prev"><ChevronLeft className="w-4 h-4" /></Button>
            <span className="font-semibold text-slate-900 font-['Barlow_Condensed'] text-lg min-w-[200px] text-center" data-testid="rem-cal-title">
              {titleLabel}
            </span>
            <Button variant="ghost" size="sm" onClick={goNext} data-testid="rem-cal-next"><ChevronRight className="w-4 h-4" /></Button>
            <Button variant="ghost" size="sm" className="text-xs" onClick={() => onAnchorChange(new Date())} data-testid="rem-cal-today">
              Today
            </Button>
          </div>
          <div className="flex h-9 rounded-md border border-slate-200 overflow-hidden">
            {['month', 'week', 'day'].map(v => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={`px-3 text-sm font-medium capitalize ${view === v ? 'bg-slate-800 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                data-testid={`rem-cal-view-${v}`}
              >
                {v}
              </button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {view !== 'day' && (
          <div className="grid grid-cols-7 gap-1 mb-2">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="text-center text-xs font-semibold text-slate-500 py-1 uppercase tracking-wide">
                {day}
              </div>
            ))}
          </div>
        )}

        <div className={view === 'day' ? '' : 'grid grid-cols-7 gap-1'}>
          {visibleDays.map((day) => {
            const dateStr = format(day, 'yyyy-MM-dd');
            const items = remindersByDay[dateStr] || [];
            const inMonth = view === 'month' ? isSameMonth(day, anchorDate) : true;
            const today = isToday(day);

            // Size by view
            let cellHeight = 'min-h-[110px] md:min-h-[140px]';
            if (view === 'week') cellHeight = 'min-h-[260px]';
            if (view === 'day')  cellHeight = 'min-h-[480px]';

            const maxVisible = view === 'month' ? 3 : 12;

            return (
              <div
                key={dateStr}
                onClick={() => onDayClick?.(day)}
                onDragOver={(e) => { e.preventDefault(); }}
                onDrop={(e) => handleDrop(e, day)}
                className={`
                  ${cellHeight} p-2 rounded-lg border cursor-pointer transition-colors
                  ${!inMonth ? 'bg-slate-50 text-slate-400 border-slate-100' : 'bg-white border-slate-200 hover:border-amber-300'}
                  ${today ? 'border-amber-500 bg-amber-50/40' : ''}
                  ${dragId ? 'ring-1 ring-amber-200' : ''}
                `}
                data-testid={`rem-cal-day-${dateStr}`}
              >
                <div className={`flex items-center justify-between mb-1`}>
                  <span className={`text-sm font-semibold ${today ? 'text-amber-600' : 'text-slate-700'}`}>
                    {view === 'day' ? format(day, 'EEEE MMM d') : format(day, 'd')}
                  </span>
                  {items.length > 0 && (
                    <span className="text-[10px] text-slate-400 font-medium">{items.length}</span>
                  )}
                </div>

                <div className="space-y-1">
                  {items.slice(0, maxVisible).map(r => (
                    <ReminderChip
                      key={r.id}
                      reminder={r}
                      view={view}
                      onClick={() => onReminderClick?.(r)}
                      onDragStart={() => setDragId(r.id)}
                      onDragEnd={() => setDragId(null)}
                    />
                  ))}
                  {items.length > maxVisible && (
                    <div className="text-xs text-slate-500 text-center font-medium" data-testid={`rem-cal-overflow-${dateStr}`}>
                      +{items.length - maxVisible} more
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function ReminderChip({ reminder, view, onClick, onDragStart, onDragEnd }) {
  const primary = extractPrimaryLink(reminder.description);
  const cat = CATEGORY_COLORS[reminder.category] || CATEGORY_COLORS.Other;

  return (
    <HoverCard openDelay={150} closeDelay={120}>
      <HoverCardTrigger asChild>
        <div
          draggable
          onDragStart={(e) => {
            e.dataTransfer.setData('text/reminder-id', reminder.id);
            e.dataTransfer.effectAllowed = 'move';
            onDragStart?.();
          }}
          onDragEnd={onDragEnd}
          onClick={(e) => { e.stopPropagation(); onClick?.(); }}
          className={`text-xs px-2 py-1 rounded border ${cat} truncate cursor-grab active:cursor-grabbing ${reminder.completed ? 'line-through opacity-60' : ''}`}
          data-testid={`rem-chip-${reminder.id}`}
        >
          <span className="font-medium">{reminder.title}</span>
          {view !== 'month' && reminder.time && (
            <span className="ml-1 opacity-70">{formatTime12(reminder.time)}</span>
          )}
        </div>
      </HoverCardTrigger>
      <HoverCardContent
        side="top"
        align="start"
        sideOffset={4}
        className="w-80 p-3"
        onClick={(e) => e.stopPropagation()}
        data-testid={`rem-chip-popover-${reminder.id}`}
      >
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <Badge className={`${cat}`}>{reminder.category}</Badge>
            {reminder.priority && (
              <span className="text-[10px] uppercase tracking-wide text-slate-500">{reminder.priority}</span>
            )}
          </div>
          <h4 className={`font-semibold text-slate-900 ${reminder.completed ? 'line-through' : ''}`}>{reminder.title}</h4>
          {reminder.description && (
            <p className="text-sm text-slate-700 whitespace-pre-wrap break-words leading-relaxed">
              {linkifyReminderText(reminder.description)}
            </p>
          )}
          <div className="flex items-center gap-3 text-xs text-slate-500 pt-1 border-t border-slate-100">
            <span className="flex items-center gap-1">
              <CalendarIcon className="w-3 h-3" />
              {format(parseISO(reminder.date), 'EEE MMM d, yyyy')}
            </span>
            {reminder.time && (
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {formatTime12(reminder.time)}
              </span>
            )}
          </div>
          {primary && (
            <a
              href={primary.url}
              target={primary.isInternal ? '_self' : '_blank'}
              rel={primary.isInternal ? undefined : 'noopener noreferrer'}
              className="mt-2 inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-amber-500 hover:bg-amber-400 text-slate-900 text-sm font-semibold transition-colors"
              data-testid="rem-chip-primary-link"
            >
              {primary.label}
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}

function formatTime12(time) {
  if (!time) return '';
  const [h, m] = time.split(':');
  const hour = parseInt(h, 10);
  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${m} ${period}`;
}
